import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { SERVER_BASE_URL } from "../Config";
import { ProductInfo } from "../AllProducts";

interface ProductManagementProps {
  onBack: () => void;
}

const ProductManagement: React.FC<ProductManagementProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfo | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form fields
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editActive, setEditActive] = useState(true);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/products`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pharmacistToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load products: ${response.statusText}`);
      }

      const data = await response.json();
      setProducts(data.products || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleEditClick = (product: ProductInfo) => {
    setSelectedProduct(product);
    setEditName(product.name);
    setEditDescription(product.description);
    setEditActive(product.active ?? true);
    setEditDialogOpen(true);
    setSaveSuccess(false);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedProduct(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedProduct) return;

    setSaving(true);
    setError(null);

    try {
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(
        `${SERVER_BASE_URL}/admin/products/${selectedProduct.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pharmacistToken}`,
          },
          body: JSON.stringify({
            name: editName,
            description: editDescription,
            active: editActive,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update product: ${response.statusText}`);
      }

      setSaveSuccess(true);
      setTimeout(() => {
        handleCloseDialog();
        loadProducts();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant={isMobile ? "h6" : "h5"}>Product Management</Typography>
        <Button variant="outlined" onClick={onBack} size="small" sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" }, whiteSpace: "nowrap", ml: 1 }}>
          {isMobile ? "Back" : "Back to Admin Dashboard"}
        </Button>
      </Box>

      {error && !editDialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {products.map((product) => (
            <Card key={product.id} variant="outlined" sx={{ cursor: "pointer" }} onClick={() => handleEditClick(product)}>
              <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
                <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {product.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      {product.type || "N/A"}
                    </Typography>
                    {product.active ?? true ? (
                      <Chip label="Visible" color="success" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                    ) : (
                      <Chip label="Hidden" color="default" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                    )}
                  </Box>
                </Box>
                <IconButton size="small" color="primary" sx={{ ml: 1 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>{product.id}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.type || "N/A"}</TableCell>
                  <TableCell>
                    {product.active ?? true ? (
                      <Chip label="Visible" color="success" size="small" />
                    ) : (
                      <Chip label="Hidden" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      startIcon={<EditIcon />}
                      size="small"
                      onClick={() => handleEditClick(product)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Product updated successfully!
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Product Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
              margin="normal"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                />
              }
              label="Product Visible to Customers"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !editName.trim()}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
